<template>
  <div class="ta-my-bids-short-list">
    <div class="ta-section-header">
      <slot name="section-header">
        <h2>{{ title }}</h2>
        <router-link v-if="lots.loaded && lots.data.length > 0" :to="{name: 'IMadeBidLots', query: queryParams}" class="ta-see-all-link">
          {{ $t('See All') }}
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
               stroke="currentColor"
               style="width: 20px">
            <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/>
          </svg>
        </router-link>
      </slot>
    </div>
    <slot v-if="!is_authorized" name="not_authorized">
      <div class="alert alert-warning text-center my-5">
        {{ $t('You must be authenticated to access this section.') }}
        <link-to-login>{{ $t('Login now') }}</link-to-login>
      </div>
    </slot>
    <div v-else-if="lots.loading" class="ta-lot-list view-type-grid">
      <div v-for="(el, i) in skeletonNumbers" :key="`skeleton-${i}`">
        <slot name="item-skeleton">
          <lot-list-item-skelleton/>
        </slot>
      </div>
    </div>
    <div v-else-if="computedLots.length" class="ta-lot-list view-type-grid">
      <i-made-bid-lot-list-item v-for="lot in computedLots" :key="lot.uuid" :lot="lot">
      </i-made-bid-lot-list-item>
    </div>
    <slot v-else name="no_items">
      <div class="alert alert-warning text-center my-5">
        {{ $t("You don't have bids on active lots.") }}
      </div>
    </slot>
  </div>
</template>

<script>
import {mapState} from 'vuex'

import axiosClient from "@/services/axios";
import IMadeBidLotListItem from "@/components/IMadeBidLotListItem.vue";
import EventBus from "@/plugins/event-bus";
import {ON_SOCKET_LOT_UPDATED, TOGGLE_LOT_WATCHLIST_STATE} from "@/plugins/event-bus/event-bus-names";
import {prepareLotDataForMutation, hydrateLots} from "@/helpers";
import {mapActions} from 'vuex';
import LotListItemSkelleton from "@/components/LotListItemSkelleton.vue";
import lotSortComparator from "@/mixins/lotSortComparator";

export default {
  name: "MyBidsShortList",
  components: {LotListItemSkelleton, IMadeBidLotListItem},
  mixins: [lotSortComparator],
  props: {
    title: {
      type: String,
      default() {
        return this.$t('My Bids')
      }
    },
    limit: {
      type: Number,
      default: 3
    },
    lotStatus: {
      type: String,
      default: () => 'all'
    }
  },
  data: () => ({
    lots: {
      loading: false,
      loaded: false,
      data: []
    },
    skeletonNumbers: []
  }),
  computed: {
    ...mapState(['is_authorized', 'lotQueryParams', 'mainConfig']),
    queryParams() {
      return {
        department: this.lotQueryParams.department,
        lot_status: this.lotStatus,
        purchase_type: 'both'
      }
    },
    computedLots() {
      if (this.mainConfig.sortByOutbidFirstInMyBids) {
        const lots = [...this.lots.data]

        lots.sort(this.getLotSortComparator)

        return lots.slice(0, this.limit);
      }

      return this.lots.data;
    }
  },
  watch: {
    'lotQueryParams.department': {
      handler() {
        this.loadLots()
      }
    }
  },
  methods: {
    ...mapActions(['subscribeToLots']),
    async loadLots() {
      if (!this.lotQueryParams.department || this.lotQueryParams.department.length === 0 || this.$route.query.q) {
        return
      }
      this.lots.loading = true;
      this.skeletonNumbers = new Array(this.limit)
      try {
        const {data, status} = await axiosClient.get(`website/lots/`, {
          params: {
            i_made_bid: 1,
            limit: this.mainConfig.sortByOutbidFirstInMyBids ? 100 : this.limit,
            lot_status: this.lotStatus,
            sort: 'end_date',
            'sort-by': 'asc',
            grouped_by_auction: 0,
            department: this.lotQueryParams.department,
            deduplicated: 1,
          }
        });
        this.lots.loading = false;
        this.lots.loaded = true;
        this.lots.data = hydrateLots(data.lots);
        this.subscribeToLots(
          this.lots.data.filter(l => l.status === 'published' || l.auction.status === 'published')
            .map(lot => lot.uuid)
        );
      } catch (e) {
        this.lots.loading = false;
      }
    },
    listenOnSocket() {
      EventBus.$on(ON_SOCKET_LOT_UPDATED, (data) => {
        this.lots.data = this.lots.data.map(lot => {
          if (lot.uuid === data.uuid) {
            data = prepareLotDataForMutation(data, lot, data.fetch)
            return {...lot, ...data}
          }
          return lot;
        });
      });
    }
  },
  async mounted() {
    this.loadLots()
    this.listenOnSocket();

    EventBus.$on(TOGGLE_LOT_WATCHLIST_STATE, ({lot_uuid, is_favored}) => {
      this.lots.data = this.lots.data.map(lot => {
        if (lot.uuid === lot_uuid) {
          lot.is_favored = !lot.is_favored
        }
        return lot;
      });
    })
  },
  destroyed() {
    EventBus.$off(ON_SOCKET_LOT_UPDATED);
    EventBus.$off(TOGGLE_LOT_WATCHLIST_STATE);
  }
}
</script>

<style lang="scss" scoped>
.ta-my-bids-short-list {
  margin-bottom: 2rem;

  .ta-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .ta-see-all-link {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .ta-lot-list-item-inner {
    display: flex;
    flex-direction: column;
  }

  .ta-lot-list {
    display: flex;
    gap: 1rem;

    &.view-type-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    }
  }
}
</style>
